import * as React from 'react'
import { datalayerHandler } from '../../../utils/datalayer'
import { useInView } from '../../../utils/hooks/hooks/useInView'
import { Image, ImageBase, ImageOptions } from '../../../utils/images'
import { Link } from '../../../utils/links'
import { maybeToMailPlusLink } from '../../../utils/mailplus'
import { featuredContentBlockClasses } from '../../../utils/style'
import { getClassName, htmlDecode } from '../../../utils/utils'
import { ActionProps, InviewProps } from './blocksState'

export type FeaturedContentBlock = {
  index: number
  imageLocation: 'left' | 'right'
  label?: string
  title: React.ReactNode
  intro: string
  primaryAction: ActionProps
  image?: ImageBase & ImageOptions
  inview?: InviewProps
  meta?: string
}

export type FeaturedContentBlockStyle = Partial<{
  blockClassName: string
  inviewClassName: string
  innerClassName: string
  contentClassName: string
  headerClassName: string
  labelClassName: string
  titleClassName: string
  textClassName: string
  introClassName: string
  footerClassName: string
  clickableWrapperClassName: string
  clickableClassName: string
  clickableLabelClassName: string
  clickableIconClassName: string
  figureClassName: string
  imageClassName: string
  imageNonExpandableClassName: string
  pictureClassName: string
  iconId: string
}>

export const FeaturedContentBlockLayout = (
  props: FeaturedContentBlock
) => {
  const {
    imageLocation,
    inview,
    index,
    label,
    title,
    intro,
    primaryAction,
    image,
    meta
  } = props
  const classes = featuredContentBlockClasses(imageLocation)
  const { inView, ref } = useInView(
    { threshold: 0.1 },
    (inview) => console.debug("FeaturedContentBlockLayout inView:", inview),
    !inview?.triggerOnce
  )
  const inviewClassName = getClassName(classes, 'blockClassName', 'container') + ' ' + (inView ? getClassName(classes, 'inviewClassName', 'inview') : '')
  return (
    <section key={`block-${index}`} id={`io-${index}`} ref={ref} className={inviewClassName}>
      <div className={getClassName(classes, 'innerClassName', 'py-5 row')}>
        <div
          className={getClassName(
            classes,
            'contentClassName',
            `col-md-6 mb-4 order-${imageLocation == 'left' ? 2 : 1}`
          )}
        >
          {(title || label) && (
            <header className={getClassName(classes, 'headerClassName', '')}>
              {label && (
                <p
                  className={getClassName(
                    classes,
                    'labelClassName',
                    'd-block small'
                  )}
                >
                  {label}
                </p>
              )}
              {title && (
                <h2
                  className={getClassName(classes, 'titleClassName', 'mb-3')}
                  id={`${index}`}
                >
                  {title}
                </h2>
              )}
            </header>
          )}

          {intro && (
            <div className={getClassName(classes, 'textClassName', '')}>
              <p className={getClassName(classes, 'introClassName', '')}>
                {intro}
              </p>
            </div>
          )}

          {/* [not CUSTOM & HARCODED anymore */}
          {meta && (
            <div className='block__meta text--xs'>
              <p>{meta}</p>
            </div>
          )}

          {primaryAction && primaryAction.title && (
            <footer className={getClassName(classes, 'footerClassName', '')}>
              <div
                className={getClassName(
                  classes,
                  'clickableWrapperClassName',
                  ''
                )}
              >
                <Link
                  url={primaryAction.url}
                  title={primaryAction.title}
                  className={getClassName(
                    classes,
                    'clickableClassName',
                    'btn btn-dark d-inline-flex'
                  )}
                  onClick={() =>
                    datalayerHandler('featured_content', primaryAction.title, 'featured_content', {
                      link: maybeToMailPlusLink(primaryAction.url)
                    })
                  }
                >
                  <span
                    className={getClassName(
                      classes,
                      'clickableLabelClassName',
                      ''
                    )}
                  >
                    {htmlDecode(primaryAction.title)}
                  </span>
                  {primaryAction.SVGIcon && (
                    <svg
                      className={getClassName(
                        classes,
                        'clickableIconClassName',
                        'ml-3'
                      )}
                      aria-hidden="true"
                      width={
                        primaryAction.SVGIcon.width
                          ? primaryAction.SVGIcon.width
                          : '24px'
                      }
                      height={
                        primaryAction.SVGIcon.height
                          ? primaryAction.SVGIcon.height
                          : '24px'
                      }
                    >
                      <use
                        xlinkHref={getClassName(
                          classes,
                          'iconId',
                          '#icon--chevron-right-large'
                        )}
                      />
                    </svg>
                  )}
                </Link>
              </div>
            </footer>
          )}
        </div>

        {image && (
          <div
            className={getClassName(
              classes,
              'figureClassName',
              `col-md-6 mb-4 order-${imageLocation == 'left' ? 1 : 2}`
            )}
          >
            <Image
              {...image}
              classNames={{
                image: getClassName(classes, (image?.keepOriginalSize ? 'imageNonExpandableClassName' : 'imageClassName'), 'w-100'),
                picture: getClassName(classes, 'pictureClassName', ''),
              }}
            />
          </div>
        )}
      </div>
    </section>
  )
}
